export interface NavigationItem {
  id: string;
  title: string;
  type: 'item' | 'collapse' | 'group';
  translate?: string;
  icon?: string;
  hidden?: boolean;
  url?: string;
  classes?: string;
  exactMatch?: boolean;
  external?: boolean;
  target?: boolean;
  breadcrumbs?: boolean;
  badge?: {
    title?: string;
    type?: string;
  };
  roles?: number[];
  /**
   * Fuente utilizada para evaluar `roles`.
   *
   * - `'legacy'` (por defecto): rol principal (`identity.rol`), vía
   *   `AuthService.isUserAllowed()`.
   * - `'assigned'`: roles múltiples asignados (`identity.roles`, provenientes
   *   de `user_role`), vía `AuthService.hasAnyAssignedRole()`.
   *
   * Se mantiene `'legacy'` por defecto para no alterar el comportamiento de
   * las entradas existentes. Las funcionalidades nuevas cuyo backend autorice
   * contra `user_role` deben preferir `'assigned'`. Este campo es el
   * precedente para la futura migración general de la autorización del menú.
   */
  roleSource?: 'legacy' | 'assigned';
  children?: NavigationItem[];
}

export const NavigationItems: NavigationItem[] = [
  {
    id: 'navigation',
    title: 'Inicio',
    type: 'group',
    icon: 'feather icon-monitor',
    children: [
      {
        id: 'dashboard',
        title: 'Dashboard',
        type: 'item',
        url: '/dashboard',
        icon: 'feather icon-home',
      },
    ],
    roles: [1, 2],
  },
  {
    id: 'area-usuarios',
    title: 'Usuarios',
    type: 'group',
    icon: 'feather icon-monitor',
    children: [
      {
        id: 'admins',
        title: 'Administradores',
        type: 'item',
        url: '/usuarios/admins',
        icon: 'fa fa-user-secret',
        roles: [1, 2],
      },
      {
        id: 'clientes',
        title: 'Clientes',
        type: 'collapse',
        icon: 'feather icon-users',
        children: [
          {
            id: 'clientes-internet',
            title: 'Clientes activos',
            type: 'item',
            url: '/servicios/servicio-clientes',
          },
          {
            id: 'clientes-inactivos',
            title: 'Clientes inactivos',
            type: 'item',
            url: '/clientes/panel-principal-inactivos',
          },
        ],
        roles: [1, 2, 4],
      },
      {
        id: 'ie',
        title: 'Instituciones',
        type: 'collapse',
        icon: 'fa fa-school',
        children: [
          {
            id: 'ie-gestion',
            title: 'I.E.',
            type: 'item',
            url: '/clientes/panel-principal-ie',
          },
          {
            id: 'ugel-gestion',
            title: 'UGEL',
            type: 'item',
            url: '/clientes/panel-principal-ugel',
          },
          {
            id: 'ie-conectividad',
            title: 'Conectividad I.E.',
            type: 'item',
            url: '/clientes/conectividad-ie',
          },
          {
            id: 'ie-reportes',
            title: 'Reportes IE',
            type: 'item',
            url: '/clientes/reportes-ie',
          },
          {
            id: 'ugel-dashboard',
            title: 'Dashboard UGEL',
            type: 'item',
            url: '/clientes/dashboard-ugel',
          },
          {
            id: 'ie-importar',
            title: 'Importar I.E.',
            type: 'item',
            url: '/clientes/importar-ie',
          },
          {
            id: 'ie-reportes-bulk',
            title: 'Descarga masiva',
            type: 'item',
            url: '/clientes/reportes-ie/descarga-masiva',
            hidden: true,
          },
        ],
        roles: [1, 2, 4],
      },
    ],
    roles: [1, 2, 4],
  },
  {
    id: 'area-soporte',
    title: 'Soporte',
    type: 'group',
    icon: 'feather icon-inbox',
    children: [
      {
        id: 'tickets',
        title: 'Tickets',
        type: 'collapse',
        icon: 'feather icon-inbox',
        children: [
          {
            id: 'tickets-summary',
            title: 'Dashboard',
            type: 'item',
            url: '/support/dashboard',
            breadcrumbs: false,
          },
          {
            id: 'tickets-list',
            title: 'Tickets',
            type: 'item',
            url: '/support/tickets',
            breadcrumbs: false,
          },
        ],
      },
    ],
    roles: [1, 2],
  },
  {
    id: 'area-redes',
    title: 'Redes',
    type: 'group',
    icon: 'feather icon-monitor',
    children: [
      {
        id: 'dispositivos',
        title: 'Dispositivos',
        type: 'collapse',
        icon: 'feather icon-server',
        children: [
          {
            id: 'routers',
            title: 'Routers',
            type: 'item',
            url: '/dispositivos/routers',
            //icon: 'feather icon-move',
            breadcrumbs: false,
          },
          {
            id: 'antenas',
            title: 'Antenas',
            type: 'item',
            url: '/dispositivos/antenas',
            //icon: 'feather icon-move',
            breadcrumbs: false,
          },
        ],
      },
      {
        id: 'ip',
        title: 'IP',
        type: 'collapse',
        icon: 'feather icon-map-pin',
        children: [
          {
            id: 'bloquesIpv4',
            title: 'Bloques de ipv4',
            type: 'item',
            url: '/ipv4-block/panel-principal',
            //icon: 'feather icon-pie-chart',
            breadcrumbs: false,
          },
        ],
      },
      {
        id: 'centrales',
        title: 'Centrales',
        type: 'item',
        url: '/sites/panel-principal',
        icon: 'fa fa-network-wired',
      },
      {
        id: 'torres',
        title: 'Torres',
        type: 'item',
        url: '/torres/panel-principal',
        icon: 'fa fa-broadcast-tower',
      },
      {
        id: 'olt',
        title: 'OLT',
        type: 'item',
        url: '/olts/panel-principal',
        icon: 'fa fa-server',
      },
      {
        id: 'cajas_nap',
        title: 'Cajas Nap',
        type: 'item',
        url: '/cajas-nap/panel-principal',
        icon: 'fa fa-box',
      },
    ],
    roles: [1, 2],
  },
  {
    id: 'area-notificaciones',
    title: 'Notificaciones',
    type: 'group',
    icon: 'feather icon-monitor',
    children: [
      {
        id: 'deudores',
        title: 'Deudas',
        type: 'collapse',
        icon: 'far fa-frown',
        children: [
          {
            id: 'internet',
            title: 'Servicio de Internet',
            type: 'item',
            url: '/notificationes/internet',
            //icon: 'feather icon-move',
            breadcrumbs: false,
          },
        ],
      },
    ],
    roles: [1, 2],
  },
  {
    id: 'area-suspensiones',
    title: 'Suspensiones',
    type: 'group',
    icon: 'feather icon-monitor',
    children: [
      {
        id: 'clientes-morosos',
        title: 'Clientes morosos',
        type: 'item',
        url: '/suspensiones/internet',
        icon: 'feather icon-wifi-off',
      },
    ],
    roles: [1, 2],
  },
  {
    id: 'area-recibos',
    title: 'Recibos',
    type: 'group',
    icon: 'feather icon-layers',
    // La restricción de roles vive en cada hijo (no en el grupo) porque
    // "Generar recibos" usa una fuente de roles distinta ('assigned') que
    // "Historial" ('legacy'). Ver NavigationItem.roleSource.
    children: [
      {
        id: 'lista_recibos',
        title: 'Historial',
        type: 'item',
        url: '/recibos/historial',
        icon: 'feather icon-file-text',
        breadcrumbs: false,
        roles: [1, 2],
      },
      {
        id: 'generar_recibos',
        title: 'Generar recibos',
        type: 'item',
        url: '/recibos/generar',
        icon: 'feather icon-plus-circle',
        breadcrumbs: false,
        // Exclusivo Super Admin, autorizado contra identity.roles (user_role),
        // no contra el rol principal legacy.
        roles: [1],
        roleSource: 'assigned',
      },
    ],
  },
  {
    id: 'area-pagos',
    title: 'Pagos',
    type: 'group',
    icon: 'feather icon-layers',
    children: [
      {
        id: 'pagos_bcp',
        title: 'Pagos BCP',
        type: 'item',
        url: '/pagos/pag-bcp',
        icon: 'feather icon-upload-cloud',
        breadcrumbs: false,
      },
      {
        id: 'historial',
        title: 'Historial',
        type: 'item',
        url: '/pago/historial',
        icon: 'feather icon-file-text',
        breadcrumbs: false,
      },
    ],
    roles: [1, 2],
  },
  {
    id: 'area-servicios',
    title: 'Servicios',
    type: 'group',
    icon: 'feather icon-layers',
    children: [
      {
        id: 'servicios',
        title: 'Servicios',
        type: 'item',
        url: '/servicios/servicios',
        icon: 'feather icon-list',
      },
      {
        id: 'planes',
        title: 'Planes',
        type: 'item',
        url: '/servicios/planes-internet',
        icon: 'feather icon-navigation',
      },
      {
        id: 'crear-cliente',
        title: 'Registrar servicios',
        type: 'item',
        url: '/servicios/serv-cli-crear',
        icon: 'feather icon-users',
        hidden: true,
      },
    ],
    roles: [1, 2],
  },
];
